import { useState } from 'react';
import { X } from 'lucide-react';
import { openDatePicker } from '@/utils/datePicker';

type StageDateCellProps = {
  value: string;
  clearLabel: string;
  todayClassName: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  onClear: () => void;
  onSetToday: () => void;
};

// Shane (pilot, 2026-07-01): while a date is being typed, the browser reports
// an incomplete value as "" — e.g. a leading 0 in the day segment. Committing
// that "" to state used to flip this cell back to "+ Today", unmounting the
// input mid-keystroke and eating the date. Keep the input mounted while it has
// focus; only collapse to "+ Today" once the field is both empty and blurred.
export function StageDateCell({
  value,
  clearLabel,
  todayClassName,
  onChange,
  onBlur,
  onClear,
  onSetToday,
}: StageDateCellProps) {
  const [focused, setFocused] = useState(false);

  if (!value && !focused) {
    return (
      <button onClick={onSetToday} className={todayClassName}>
        + Today
      </button>
    );
  }

  return (
    <div className="flex items-center gap-0.5">
      <input
        type="date"
        onClick={openDatePicker}
        value={value}
        onFocus={() => setFocused(true)}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          setFocused(false);
          onBlur();
        }}
        className="flex-1 min-w-0 text-xs border border-slate-200 dark:border-slate-600 rounded px-1.5 py-1 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 [&::-webkit-calendar-picker-indicator]:hidden sm:[&::-webkit-calendar-picker-indicator]:inline-block"
      />
      <button
        type="button"
        onClick={onClear}
        title={clearLabel}
        aria-label={clearLabel}
        className="shrink-0 p-0.5 text-slate-400 hover:text-red-500"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
